import {
  InteractiveContainer,
  LoadingSpinner,
  DelayedRender,
} from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import { formatSecondsToCompactDuration } from "@jugl-web/utils/date-time/formatSecondsToCompactDuration";
import React, { useCallback, useMemo, useRef } from "react";
import { useMediaPlayer } from "@web-src/modules/media/providers/MediaPlayerProvider/useMediaPlayer";
import { useUnmount } from "react-use";
import { ReactComponent as PlayIcon } from "./assets/play.svg";
import { ReactComponent as PauseIcon } from "./assets/pause.svg";
import { RangeSlider } from "./components/RangeSlider";

export const ChatAudioPlayer: React.FC<{
  src: string;
  name: string;
  variant: "incoming" | "outgoing";
  duration?: number;
}> = ({ src, name, variant, duration }) => {
  const {
    currentAudio,
    playAudio,
    pauseAudio,
    resumeAudio,
    setCurrentTime,
    stopAudio,
  } = useMediaPlayer();
  const timeRef = useRef<HTMLSpanElement>(null);
  const isOutgoing = variant === "outgoing";

  const isThisAudioLoaded = currentAudio?.src === src;
  const isThisAudioPlaying = isThisAudioLoaded && currentAudio.isPlaying;
  const playingDuration = isThisAudioLoaded ? currentAudio.currentTime : 0;

  const fullDuration = isThisAudioLoaded
    ? currentAudio.duration
    : duration || 0;

  const togglePlayPause = useCallback(() => {
    if (isThisAudioPlaying) {
      pauseAudio();
    } else if (isThisAudioLoaded) {
      resumeAudio();
    } else {
      if (currentAudio) {
        stopAudio();
      }
      playAudio(src, name, fullDuration);
    }
  }, [
    isThisAudioPlaying,
    isThisAudioLoaded,
    pauseAudio,
    resumeAudio,
    currentAudio,
    playAudio,
    src,
    name,
    stopAudio,
    fullDuration,
  ]);

  const handleProgressChange = (value: number) => {
    if (isThisAudioLoaded) {
      setCurrentTime(value);
    }
  };

  const buttonClassNames = useMemo(() => {
    if (isOutgoing) {
      if (isThisAudioPlaying) {
        return "bg-white text-gradients-g3 hover:bg-grey-200";
      }
      return "bg-white text-primary hover:bg-grey-200";
    }
    if (isThisAudioPlaying) {
      return "bg-gradients-g3 text-white hover:bg-[#9D4AD7]";
    }
    return "bg-primary text text-white hover:bg-primary-600";
  }, [isThisAudioPlaying, isOutgoing]);

  useUnmount(() => {
    if (isThisAudioLoaded) {
      stopAudio();
    }
  });

  return (
    <div className="flex items-center gap-2.5 p-2">
      <InteractiveContainer
        onClick={togglePlayPause}
        className={cx(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary transition-colors",
          buttonClassNames
        )}
      >
        <DelayedRender
          isLoading={isThisAudioPlaying && !!currentAudio?.isLoading}
          loadingContent={<LoadingSpinner className="h-5 w-5" />}
        >
          {isThisAudioPlaying ? <PauseIcon /> : <PlayIcon />}
        </DelayedRender>
      </InteractiveContainer>
      <div className="flex grow flex-col gap-0.5 overflow-hidden">
        <span
          className={cx(
            "truncate font-medium",
            isOutgoing ? "text-white" : "text-dark"
          )}
        >
          {name}
        </span>
        <div className="flex items-center gap-2">
          <span
            className={cx("text-sm", isOutgoing ? "text-white" : "text-grey")}
            ref={timeRef}
          >
            {formatSecondsToCompactDuration(
              isThisAudioLoaded ? playingDuration : fullDuration,
              { padLeadingUnit: true }
            )}
          </span>
          {isThisAudioLoaded ? (
            <RangeSlider
              onChange={handleProgressChange}
              variant={variant}
              min={0}
              max={fullDuration || 0}
              step={0.0001}
              value={playingDuration || 0}
            />
          ) : (
            <div className="w-[176px]" />
          )}
        </div>
      </div>
    </div>
  );
};
